import { CircularProgress } from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import FormatService from 'services/formatService';
import { useGetActivityFeedLogByWorkOrderIdQuery } from 'features/activity/activitySlice';
import { useGetAllIssuesTrackerQuery } from 'features/IssuesTracker/issuesTrackerSlice';
import { activityTimelineStyles } from './ActivityTimelineStyles';
import { activityTimelineMessages, creationFeedLogTypes, entryIconByType, ticketFeedLogTypes, WO_LEVEL_LOGS_LAUNCH_DATE } from './ActivityTimelineConsts';

const ActivityTimeline = ({ workOrderId, vehicleId, workOrderCreatedDate }) => {
    const classes = activityTimelineStyles();
    const history = useHistory();

    const { data: entries = [], isLoading, isError } = useGetActivityFeedLogByWorkOrderIdQuery(workOrderId, { skip: !workOrderId });
    const { data: tickets } = useGetAllIssuesTrackerQuery({ includeIssueTrackerHistory: false, vehicleId }, { skip: !vehicleId });

    const linkedTicketId = useMemo(
        () => tickets?.find(t => t?.linkedWorkOrders?.some(wo => wo?.id === workOrderId))?.id,
        [tickets, workOrderId]
    );

    const sortedEntries = useMemo(
        () => [...entries].sort((a, b) => {
            const aTime = moment(a.createdDate).valueOf();
            const bTime = moment(b.createdDate).valueOf();
            const aIsCreation = creationFeedLogTypes.includes(a.type);
            const bIsCreation = creationFeedLogTypes.includes(b.type);
            if (aIsCreation !== bIsCreation && Math.abs(aTime - bTime) <= 60000) {
                return aIsCreation ? -1 : 1;
            }
            return aTime - bTime;
        }),
        [entries]
    );

    const renderBody = () => {
        if (isLoading) {
            return <CircularProgress size={20} />;
        }
        if (isError) {
            return <div className={classes.errorMessage}>{activityTimelineMessages.error}</div>;
        }
        if (sortedEntries.length === 0) {
            const isLegacy = workOrderCreatedDate && moment(workOrderCreatedDate).isBefore(moment(WO_LEVEL_LOGS_LAUNCH_DATE));
            return <div className={classes.stateMessage}>{isLegacy ? activityTimelineMessages.legacy : activityTimelineMessages.empty}</div>;
        }
        return (
            <ul className={classes.list}>
                {sortedEntries.map(entry => {
                    const isTicket = ticketFeedLogTypes.includes(entry.type);
                    const clickable = isTicket && !!linkedTicketId;
                    const EntryIcon = entryIconByType[entry.type];
                    return (
                        <li
                            key={entry.id}
                            className={`${classes.entry} ${clickable ? classes.ticketEntry : ''}`}
                            onClick={clickable ? () => history.push(`/ticket/${linkedTicketId}`) : undefined}
                        >
                            <span className={classes.marker}>
                                {EntryIcon
                                    ? <EntryIcon fontSize="small" />
                                    : <FiberManualRecordIcon className={classes.fallbackIcon} />}
                            </span>
                            <div className={classes.body}>
                                <div className={classes.description}>{entry.description}</div>
                                <div className={classes.meta}>
                                    {entry.createdBy || 'System'}
                                    {entry.createdDate ? ` · ${FormatService.formatDateTime(entry.createdDate)}` : ''}
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
        );
    };

    return (
        <div className={classes.panel}>
            <div className={classes.title}>Activity Timeline</div>
            {renderBody()}
        </div>
    );
};

export default ActivityTimeline;
